import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useExcRouteV1RoutingRuleQueryServiceRoutingRuleGet } from '@interxfi/api-client/generated/hooks/exc-route-v1-routing-rule-query-service-routing-rule-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcRouteV1RoutingRuleState } from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { RouteRoutingDestinationsTable } from '../../../../../../../../../../features/fix/route/route-routing-destinations-table';
import { RouteRuleEntityMenu } from '../../../../../../../../../../features/fix/route/route-rule-entity-menu';
import { RouteRuleEventSummary } from '../../../../../../../../../../features/fix/route/route-rule-event';
import { getRouteRoutingRuleStateListItems } from '../../../../../../../../../../features/fix/route/route-rule-list-items';
import { getRouteRuleDetailPath } from '../../../../../../../../../../features/fix/route/route-rule-routing';
import { RouteRuleStatusPill } from '../../../../../../../../../../features/fix/route/route-rule-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/oms/routing-rule/_routing-rule/$ruleId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `oms-route-rule-${params.ruleId}`,
        label: 'Routing Rule',
        to: getRouteRuleDetailPath(params.ruleId),
        suffix: <CopyButton content={params.ruleId} label="Copy Rule ID" tooltip={{ copy: params.ruleId, copied: 'Copied Rule ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Routing Rule'),
  component: RoutingRuleDetail,
});

function getListItems(data: ExcRouteV1RoutingRuleState | undefined) {
  const listItems = getRouteRoutingRuleStateListItems(data);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [listItems.data.priority, listItems.data.failoverBehavior],
        },
        {
          id: 'filter',
          title: 'Filter',
          items: [
            listItems.data.filter.type.typeName,
            ...match(listItems.data.filter.type)
              .with({ matchAll: P.not(P.nullish) }, () => [])
              .with({ priceFilter: P.not(P.nullish) }, () => [])
              .with({ symbolFilter: P.not(P.nullish) }, (f) => [f.symbolFilter.value.symbols])
              .otherwise(() => []),
          ],
        },
      ] as const satisfies ReadonlyListGroup[],
      right: [listItems.metadata.createdAt, listItems.metadata.updatedAt, listItems.ruleId] as const satisfies ReadonlyListItem[],
    },
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load routing rule', 'view this routing rule');

function RoutingRuleDetail() {
  const { ruleId } = Route.useParams();
  const request = { ruleId } as const;
  const { data, error, isPending } = useExcRouteV1RoutingRuleQueryServiceRoutingRuleGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.routingRule), [data?.routingRule]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <RouteRuleStatusPill isPending={showPending} size="large" status={data?.routingRule.status} />
          </HorizontalStack>

          <RouteRuleEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <RouteRuleEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Destinations</Typography.Level3>

          <RouteRoutingDestinationsTable destinations={data?.routingRule.data?.destinations ?? STABLE_EMPTY_ARRAY_REF} showSkeleton={showPending} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
